import type { GameAnalyticsEvent } from 'vue3-game-analytics';
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';

import { useGamePlayStore } from '@/stores/gamePlayStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';

import { addBreadcrumb, getBreadcrumbs } from './breadcrumbs';

function isUnsetOrDefault(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    value === 'default'
  );
}

function coerceRouteName(name: RouteLocationNormalizedLoaded['name']) {
  return name ? String(name) : null;
}

function sanitizeRouteParams(params: Record<string, string | string[]>) {
  const sensitiveKeys = new Set(['token']);
  const sanitized: Record<string, string | string[]> = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    if (sensitiveKeys.has(key)) {
      sanitized[key] = '[redacted]';
      return;
    }

    sanitized[key] = value;
  });

  return sanitized;
}

function sanitizeRoutePath(route: RouteLocationNormalizedLoaded) {
  // Avoid leaking sensitive path params (e.g. password reset tokens).
  let path = route.path;

  const sensitiveKeys = new Set(['token']);
  Object.entries(route.params || {}).forEach(([key, value]) => {
    if (!sensitiveKeys.has(key)) return;

    const values = Array.isArray(value) ? value : [value];
    values.forEach((v) => {
      if (typeof v !== 'string' || !v) return;

      // Replace all occurrences without regex to avoid escaping issues.
      path = path.split(v).join(`:${key}`);
    });
  });

  return path;
}

function getRouteTemplate(route: RouteLocationNormalizedLoaded) {
  const lastMatch = route.matched?.[route.matched.length - 1];
  return lastMatch?.path ?? null;
}

function isGameplayRoute(route: RouteLocationNormalizedLoaded) {
  return coerceRouteName(route.name) === 'gameplay';
}

export function createAnalyticsEventTransformer(router: Router) {
  return (event: GameAnalyticsEvent): GameAnalyticsEvent => {
    // Snapshot breadcrumbs BEFORE we add this event.
    const isErrorEvent =
      event.type === 'error' ||
      event.type === 'error_screen_shown' ||
      !!event.error;
    const breadcrumbSnapshot = isErrorEvent
      ? getBreadcrumbs().slice(-20)
      : null;

    try {
      const route = router.currentRoute.value;
      const gameplay = isGameplayRoute(route);

      const userStore = useUserStore();
      const sessionStore = useSessionStore();
      const gamePlayStore = useGamePlayStore();
      const uiStore = useUIStore();

      const userId = userStore.auth?.user_id ?? userStore.user?.id ?? null;

      const storeGameId = gameplay ? (gamePlayStore.gameId ?? null) : null;
      const storePlayId = sessionStore.play_id ?? null;

      const resolvedGameId =
        isUnsetOrDefault(event.gameId) && !isUnsetOrDefault(storeGameId)
          ? storeGameId
          : event.gameId;

      const resolvedPlayId =
        isUnsetOrDefault(event.playId) && !isUnsetOrDefault(storePlayId)
          ? storePlayId
          : event.playId;

      const routeName = coerceRouteName(route.name);
      const routeTemplate = getRouteTemplate(route);

      const baseEnvironmentData = {
        uri: sanitizeRoutePath(route),
        routeName,
        routeTemplate,
        routeParams: sanitizeRouteParams(
          route.params as Record<string, string | string[]>
        ),
        buildMode: import.meta.env.MODE,
      };

      const baseMetadata = {
        userId,
        uiStep: uiStore.currentStep ?? null,
      };

      const baseGameState = gameplay
        ? {
            level: gamePlayStore.level ?? undefined,
            round: gamePlayStore.currentRound ?? undefined,
            trial: gamePlayStore.currentTrial ?? undefined,
            gameMode: gamePlayStore.gameMode ?? undefined,
            score: gamePlayStore.score ?? undefined,
          }
        : {};

      const transformed: GameAnalyticsEvent = {
        ...event,
        gameId: resolvedGameId,
        playId: resolvedPlayId,
        environmentData: {
          ...(event.environmentData || {}),
          ...baseEnvironmentData,
        },
        metadata: {
          ...baseMetadata,
          ...(event.metadata || {}),
          ...(breadcrumbSnapshot ? { breadcrumbs: breadcrumbSnapshot } : {}),
        },
        gameState: {
          ...baseGameState,
          ...(event.gameState || {}),
        },
      };

      // Add a lightweight breadcrumb for this event.
      addBreadcrumb({
        ts: transformed.timestamp,
        kind: 'analytics',
        type: transformed.type,
        target: transformed.target ?? null,
        data: {
          gameId: transformed.gameId,
          playId: transformed.playId,
          level: transformed.gameState?.level,
          round: (transformed.gameState as Record<string, unknown>)?.round,
          trial: (transformed.gameState as Record<string, unknown>)?.trial,
        },
      });

      return transformed;
    } catch (error) {
      // If enrichment fails, still keep event tracking alive.
      console.warn('[Analytics] Failed to enrich event', error);

      // Still add a breadcrumb so we have some context for later errors.
      try {
        addBreadcrumb({
          ts: event.timestamp,
          kind: 'analytics',
          type: event.type,
          target: event.target ?? null,
        });
      } catch {
        // no-op
      }

      // Attach breadcrumbs to error events even in failure mode.
      if (breadcrumbSnapshot) {
        return {
          ...event,
          metadata: {
            ...(event.metadata || {}),
            breadcrumbs: breadcrumbSnapshot,
          },
        };
      }

      return event;
    }
  };
}
